import { css } from '@emotion/react'
import { Card, colors, font, mq } from 'plume-ui'
import React, { useRef, useState, useEffect } from 'react'
import { fetchCompanyNewsFeed } from '@/lib/api/company'
import { printError } from '@/lib/log'
import { scrollToRef, usePageChange } from '@/lib/page'
import {
  ExternalNewsData,
  FeedData,
  NewsFeedData,
} from '../../../../../types/api'
import { CardInnerTitle } from '../../../../atoms/CardInnerTitle'
import { Pagination } from '../../../../common/Pagination'
import { FormattedDate } from '../../../../atoms/FormattedDate'

type Props = {
  shortname: string
  className?: string
  feed: NewsFeedData[]
}

export const CompanyNewsFeed: React.FC<Props> = (props) => {
  const ref = useRef<HTMLDivElement>(null)
  const [feed, setFeed] = useState({
    data: props.feed ?? [],
    count: 0,
  })
  const [page, setPage] = useState(1)

  const loadCompanyNewsFeed = (page: number) => {
    const offset = (page - 1) * 10
    return fetchCompanyNewsFeed(props.shortname, { offset })
      .then((f) => setFeed(f))
      .catch((err) => printError(err))
  }

  const onPageChange = (page: number) => {
    return loadCompanyNewsFeed(page)
  }

  useEffect(() => {
    setPage(1)
    if (page === 1) {
      loadCompanyNewsFeed(1)
    }
  }, [props.shortname])

  usePageChange({
    page,
    onChange: onPageChange,
    afterChange: () => scrollToRef({ ref, offset: 230 }),
    dispatchOnMount: true,
  })

  const LIMIT = 10

  if (feed.data.length === 0) {
    return null
  }

  return (
    <div ref={ref}>
      <Card className={props.className}>
        <CardInnerTitle title="外部ニュース" />
        {feed.data.map((n, i) => (
          <NewsFeedItem news={n} key={i} />
        ))}
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            padding-top: 15px;
            padding-bottom: 15px;
          `}
        >
          <Pagination
            limit={LIMIT}
            count={feed.count}
            page={page}
            setPage={setPage}
          />
        </div>
      </Card>
    </div>
  )
}

const NewsFeedItem: React.FC<{
  news: NewsFeedData
}> = (props) => {
  const url = isExternalNews(props.news) ? props.news.url : props.news.path

  const src = isExternalNews(props.news)
    ? props.news.imageUrl
    : feedImg(props.news)

  return (
    <div
      css={css`
        padding: 15px;
        border-bottom: 1px solid ${colors.gray50};
        display: flex;
        flex-direction: row-reverse;
        grid-gap: 16px;
        ${mq.large} {
          flex-direction: row;
        }
      `}
    >
      <div
        css={css`
          flex-basis: 112px;
          flex-shrink: 0;
          ${mq.large} {
            flex-basis: 144px;
          }
        `}
      >
        <a
          href={url}
          target="_blank"
          css={css`
            display: block;
            position: relative;
            border-radius: 2px;
            &::before {
              content: '';
              display: block;
              width: 100%;
              height: 100%;
              background-color: transparent;
              border-radius: 2px;
              position: absolute;
              transition: background-color 0.2s ease;
            }
            &:hover::before {
              background-color: rgba(0, 0, 0, 0.08);
            }
          `}
        >
          <Image src={src} />
        </a>
      </div>
      <div>
        <div>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            css={css`
              font-size: 15px;
              font-weight: ${font.weight.bold};
              line-height: 1.5;
              color: ${colors.black10};
              font-size: 14px;
              text-decoration: underline solid transparent;
              transition: text-decoration 0.2s ease 0s;
              &:hover {
                text-decoration: underline solid Currentcolor;
              }
              ${mq.large} {
                font-size: 14px;
              }
            `}
          >
            {props.news.title}
          </a>
        </div>
        <div
          css={css`
            margin-top: 4px;
            font-size: 12px;
            line-height: 1.25;
            color: ${colors.textLight};
          `}
        >
          <FormattedDate>{props.news.publishedAt}</FormattedDate>
        </div>
      </div>
    </div>
  )
}

const fallbackSrc = '/static/images/no-image.png'

const Image: React.FC<{ src?: string }> = (props) => {
  const [{ src, error }, setState] = React.useState({
    src: props.src || fallbackSrc,
    error: false,
  })

  React.useEffect(() => {
    setState({ src: props.src || fallbackSrc, error: false })
  }, [props.src])

  const handleError = React.useCallback(() => {
    setState({ src: fallbackSrc, error: true })
  }, [error])

  return (
    <img
      alt="news feed image"
      src={src}
      onError={handleError}
      css={css`
        display: block;
        object-fit: contain;
        width: 112px;
        aspect-ratio: 1.91 / 1;
        border-radius: 2px;
        ${mq.large} {
          width: 144px;
        }
      `}
    />
  )
}

const feedImg = (feed: FeedData) => {
  switch (feed.feedType) {
    case 'notification':
      return '/static/images/feed/feed_notification.png'
    default:
      return '/static/images/no-image.png'
  }
}

const isExternalNews = (item: any): item is ExternalNewsData => {
  return item.url !== undefined
}
